"use client";
import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Card,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StoreInfo from "./StoreInfo";
import SubscriptionTypeClean from "./SubscriptionTypeClean";
import ChatbotSettings from "./ChatbotSetting";
import BrandingAppearance from "./BrandingAppearance";
import ManageSeatsModal from "./ManageSeatsModal";
import ManageLocationsModal from "./ManageLocationsModal";
import PlanChangeModal from "./PlanChangeModal";
import AddLocationDetailsModal from "./AddLocationDetailsModal";
import { useNavigate, useParams } from "react-router-dom";
import config from "../../../../config";
import { AuthContext } from "../../../../authentication/AuthContext";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ p: 3 }}>{children}</Box> : null;
}

const EditStore = () => {
  const navigate = useNavigate();
  const { checkSubscriptionStatus } = useContext(AuthContext);
  const { store_id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [tokenPlans, setTokenPlans] = useState([]);
  const CLIENT_ID = config.clientId;
  const [subTab, setSubTab] = useState(0);
  const [selectedTokenPack, setSelectedTokenPack] = useState(null);


  // Loading states
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  // Store Info States
  const [storeName, setStoreName] = useState("");
  const [storeTypes, setStoreTypes] = useState([]);
  const [zipCodes, setZipCodes] = useState([]);
  const [numLocations, setNumLocations] = useState(1);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
    const [existingPlan, setExistingPlan] = useState("");

  const [planAmount, setPlanAmount] = useState(0);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [nextDueDate, setNextDueDate] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(true);

  const [selectedPlan, setSelectedPlan] = useState(existingPlan);
  const [previousPlan, setPreviousPlan] = useState(existingPlan);

  const [plansData, setPlansData] = useState(null);
  const [currentPlanData, setCurrentPlanData] = useState(null);

  // Modal States
  const [showManageSeatsModal, setShowManageSeatsModal] = useState(false);
  const [showManageLocationsModal, setShowManageLocationsModal] = useState(false);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [prorationAmount, setProrationAmount] = useState(0);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [purchaseIntentId, setPurchaseIntentId] = useState(null);



  // Chatbot States
  const [chatbotData, setChatbotData] = useState({
    chatbot_name: "",
    chatbot_welcome_message: "",
    chatbot_disclaimer: "",
  });

  // Branding States
  const [brandingData, setBrandingData] = useState({
    chatbot_theme_color: "",
    chatbot_font_style: "",
  });

  // Track changes for save button
  const [storeInfoChanged, setStoreInfoChanged] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState(null);

  // Check if plan allows chatbot/branding editing
  const isPremiumOrAbove = ["Premium", "Pro"].includes(selectedPlan);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };


// ------------------------------------------------------
  // TOKEN PURCHASE: CORE WORKFLOW
  // ------------------------------------------------------
  const handleTokenPurchase = async () => {
    if (!selectedTokenPack) {
      showSnackbar("Please select a token pack", "warning");
      return;
    }

    try {
      setLoading(true);
      showSnackbar("Redirecting to payment...", "info");

      const res = await fetch(
        `${config.API_BASE_V2_URL}/invoices/plans/onetime`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            store_id: store_id,
            subscription_id: subscriptionId,
            plan_id: selectedTokenPack.plan_id,
          }),
        }
      );

      const json = await res.json();

      if (!json.success || !json.pay_url) {
        showSnackbar("Failed to generate payment link", "error");
        return;
      }

      setPaymentUrl(json.pay_url);
      setIframeLoading(true)
      setPurchaseIntentId(json.intent_id);
    } catch (err) {
      console.error(err);
      showSnackbar("Token purchase failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTokenPlans = async () => {
    try {
      const res = await fetch(
        `${config.API_BASE_V2_URL}/invoices/token-plans`
      );
      const json = await res.json();

      if (json.success) {
        setTokenPlans(json.plans);
      }
    } catch (err) {
      console.error("Failed to load token plans:", err);
    }
  };

  // ------------------------------------------------------
  // STORE INFO SAVE
  // ------------------------------------------------------
  const handleSaveStoreInfo = async () => {
    try {
      const payload = {
        client_id: CLIENT_ID,
        section: "store_info",
        data: {
          store_name: storeName.trim(),
          store_types: storeTypes,
          number_of_locations: numLocations.toString(),
          location_zip_codes: zipCodes,
          contact_person_name: contactName.trim(),
          contact_email_id: contactEmail.trim(),
          domain_url: storeUrl.trim(),
        },
      };

      const res = await fetch(
        `${config.API_BASE_URL}/client/store/${store_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message || "Failed to update store info");
      }

      setStoreInfoChanged(false);
      showSnackbar("Store info updated successfully", "success");
    } catch (error) {
      console.error("Error updating store info:", error);
      showSnackbar(error.message || "Failed to update store info", "error");
    }
  };

  // ------------------------------------------------------
  // PLAN SELECTION + MODAL
  // ------------------------------------------------------
  const handlePlanSelect = (planObj) => {
    // backup current plan so we can revert on cancel
    setPreviousPlan(selectedPlan);
    setSelectedPlan(planObj.plan_name);

    if (planObj.plan_name !== existingPlan) {
      setPendingPlan(planObj);
      setShowPlanChangeModal(true);
    }
  };

  // ------------------------------------------------------
  // PRORATION CONFIRM → TRIGGER UPGRADE
  // (uses Option B proration format)
  // ------------------------------------------------------
  /**
   * Handles plan change confirmation from modal
   * Modal shows all details, user confirms, then immediately triggers payment
   */
  const handleProrationConfirm = async (newPlan, prorationData) => {
    try {
      setLoading(true);

      const prorationAmt = Number(prorationData.difference_cents / 100);

      setProrationAmount(prorationAmt);

      // Visually apply new plan
      setSelectedPlan(newPlan.plan_name);

      // Inject proration into plansData for summary UI
      setPlansData((prev) =>
        prev
          ? prev.map((p) =>
              p.plan_id === newPlan.plan_id
                ? { ...p, prorationAmount: prorationAmt }
                : p
            )
          : prev
      );

      setShowPlanChangeModal(false);

      showSnackbar(
        `Proration of $${prorationAmt} applied. Redirecting to payment...`,
        "info"
      );

      // Trigger upgrade API immediately after confirmation
      const payload = {
        store_id: store_id,
        subscription_id: subscriptionId,
        new_plan_id: newPlan.plan_id,
        proration_amount: prorationAmt,
      };

      const response = await fetch(
        `${config.API_BASE_V2_URL}/subscriptions/plan-change`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const json = await response.json();

      if (!json.success) {
        showSnackbar(
          json.error || "Failed to initiate upgrade. Please try again.",
          "error"
        );
        return;
      }

      // Backend returns payment_link
      if (json.payment_link) {
        setSubscriptionId(json.subscription_id);
        setPaymentUrl(json.payment_link);
      } else if (json.link) {
        setSubscriptionId(json.subscription_id);
        // fallback if API still uses `link`
        setPaymentUrl(json.link);
      } else {
        showSnackbar("Could not retrieve payment link.", "error");
      }
    } catch (error) {
      console.error("Upgrade error:", error);
      showSnackbar("Unexpected error during upgrade.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------------------------------
  // DATA FETCHING
  // ------------------------------------------------------
  useEffect(() => {
    fetchPlansData();
  }, [store_id]);

  useEffect(() => {
    if (plansData) fetchStoreData();
  }, [plansData, store_id]);

  useEffect(() => {
    fetchTokenPlans();
  }, []);

  const fetchPlansData = async () => {
    try {
      const response = await fetch(
        `${config.API_BASE_V2_URL}/subscriptions/plans`
      );
      const result = await response.json();

      if (result.success && result.data) {
        const sorted = result.data.sort((a, b) => {
          const order = ["Basic", "Pro", "Premium"];
          return order.indexOf(a.plan_name) - order.indexOf(b.plan_name);
        });
        setPlansData(sorted);
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load plans", "error");
    }
  };

  const fetchStoreData = async () => {
    try {
      setLoading(true);
      const url = `${config.API_BASE_URL}/client/store/${store_id}?client_id=${CLIENT_ID}`;
      const res = await fetch(url);
      const json = await res.json();

      if (!json.success) throw new Error(json.message);

      const data = json.data;
      setStoreName(data.store_name || "");
      setStoreTypes(data.store_types || []);
      setZipCodes(data.location_zip_codes || []);
      setNumLocations(+data.number_of_locations || 1);
      setContactName(data.contact_person_name || "");
      setContactEmail(data.contact_email_id || "");
      setStoreUrl(data.domain_url || "");

      setSubscriptionId(data.subscription_id);
      setSelectedPlan(data.plan_name);
      setPreviousPlan(data.plan_name);
      setExistingPlan(data.plan_name);
      setPlanAmount(data.plan_amount);
      setBillingCycle(data.billing_cycle);
      setSubscriptionStatus(data.subscription_status || "");
      setNextDueDate(data.next_due_date || null);

      const current = plansData?.find(
        (p) => p.plan_name === data.plan_name
      );
      setCurrentPlanData(current || null);

      // Chatbot
      setChatbotData({
        chatbot_name: data.chatbot_name,
        chatbot_welcome_message: data.chatbot_welcome_message,
        chatbot_disclaimer: data.chatbot_disclaimer,
      });

      // Branding
      setBrandingData({
        chatbot_theme_color: data.chatbot_theme_color,
        chatbot_font_style: data.chatbot_font_style,
      });
    } catch (err) {
      console.error(err);
      showSnackbar(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!paymentUrl) return;

    let intervalId;

    const fetchStatus = async () => {
      try {
        // Route based on upgrade vs token purchase
        const url =
          subscriptionId && !purchaseIntentId
            ? `${config.API_BASE_V2_URL}/subscriptions/${subscriptionId}`
            : `${config.API_BASE_V2_URL}/invoices/payment-intents/${purchaseIntentId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        const result = await response.json();
        console.log("Payment status check:", result);

        const data = result.data || {};

        // 1️⃣ Upgrade success
        const isUpgradeCaptured =
          data.subscription_status === "active" ||
          data.status === "active";

        // 2️⃣ Token purchase success
        const isTokenPurchaseCompleted =
          data.otp_status === "completed" ||
          (data.status === "captured" && data.purchase_id);

        if (isUpgradeCaptured || isTokenPurchaseCompleted) {
          // 🔥 Reset all proration UI after payment success
          setPlansData((prev) =>
            prev
              ? prev.map((p) => ({
                  ...p,
                  prorationAmount: 0,
                }))
              : prev
          );
          setProrationAmount(0);

          // Snackbar fix
          showSnackbar("Payment completed successfully!", "success");

          // UI reset
          setPaymentStatus("active");
          setShowPaymentSuccess(true);
          setPaymentUrl(null); // close iframe
          clearInterval(intervalId);
          // Refresh store data after upgrade / token purchase
          fetchStoreData();
          setPreviousPlan(null);
        }
      } catch (err) {
        console.error("Error fetching payment status:", err);
      }
    };

    fetchStatus();
    intervalId = setInterval(fetchStatus, 8000);

    return () => clearInterval(intervalId);
  }, [paymentUrl, subscriptionId, purchaseIntentId]);

  // ------------------------------------------------------
  // Payment iframe view (common for upgrade + tokens)
  // ------------------------------------------------------
  if (paymentUrl) {
    return (
      <Box>

        <Card sx={{ p: 2, height: "80vh", mt: 2 }}>
          {iframeLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255,255,255,0.8)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 2,
                }}
              >
                <CircularProgress />
              </Box>
          )}
          <iframe
            src={paymentUrl}
            style={{ width: "100%", height: "100%", border: "none" }}
            title="Payment"
            onLoad={() => setIframeLoading(false)}
          />
        </Card>

        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => {
            setPaymentUrl(null);
            fetchStoreData();
          }}
        >
          Back
        </Button>
      </Box>
    );
  }

  // ------------------------------------------------------
  // Main Render
  // ------------------------------------------------------
  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Card sx={{ boxShadow: "none", mb: "25px", p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight="700">
          Edit Store
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/store-management")}
        >
          Back
        </Button>
      </Box>

      {/* Main Tabs */}
      <Tabs
  value={activeTab}
  onChange={(_, v) => {
    // Prevent clicking Chatbot Settings (index 2) and Branding (index 3)
    if (v === 2 || v === 3) return;
    setActiveTab(v);
  }}
>
        <Tab label="Store Info" />
        <Tab label="Subscription" />
        <Tab label="Chatbot Settings" />
        <Tab label="Branding" />
      </Tabs>

      {/* -------------------------------------------------- */}
      {/* STORE INFO TAB */}
      {/* -------------------------------------------------- */}
      <CustomTabPanel value={activeTab} index={0}>
        <StoreInfo
          storeName={storeName}
          setStoreName={(v) => {
            setStoreName(v);
            setStoreInfoChanged(true);
          }}
          storeTypes={storeTypes}
          setStoreTypes={(v) => {
            setStoreTypes(v);
            setStoreInfoChanged(true);
          }}
          zipCodes={zipCodes}
          setZipCodes={(v) => {
            setZipCodes(v);
            setStoreInfoChanged(true);
          }}
          numLocations={numLocations}
          setNumLocations={(v) => {
            setNumLocations(v);
            setStoreInfoChanged(true);
          }}
          contactName={contactName}
          setContactName={(v) => {
            setContactName(v);
            setStoreInfoChanged(true);
          }}
          contactEmail={contactEmail}
          setContactEmail={(v) => {
            setContactEmail(v);
            setStoreInfoChanged(true);
          }}
          storeUrl={storeUrl}
          setStoreUrl={(v) => {
            setStoreUrl(v);
            setStoreInfoChanged(true);
          }}
          isEdit={true}
        />

        <Stack direction="row" justifyContent="space-between" mt={2}>
          <Button
            variant="contained"
            onClick={handleSaveStoreInfo}
            disabled={!storeInfoChanged}
          >
            Save Changes
          </Button>

          <Button variant="contained" onClick={() => setActiveTab(1)}>
            Next
          </Button>
        </Stack>
      </CustomTabPanel>

      {/* -------------------------------------------------- */}
      {/* SUBSCRIPTION TAB */}
      {/* -------------------------------------------------- */}
      <CustomTabPanel value={activeTab} index={1}>
        {/* Inner Tabs */}
        {/* <Tabs
          value={subTab}
          onChange={(_, v) => setSubTab(v)}
          sx={{ mb: 2 }}
        >
          <Tab label="Regular Subscription" />
          <Tab label="One-Time Token Purchase" />
        </Tabs> */}
        <Tabs
  value={subTab}
  onChange={(_, v) => setSubTab(v)}
  centered
  sx={{
    mb: 3,
    borderBottom: "1px solid #e2e8f0",
    "& .MuiTabs-indicator": {
      display: "none",
    },
  }}
>
  <Tab
    label="Regular Subscription"
    sx={{
      textTransform: "none",
      fontSize: "15px",
      fontWeight: subTab === 0 ? 700 : 500,
      color: subTab === 0 ? "#0f172a" : "#64748b",
      paddingY: 1.5,
      minWidth: 200,

      borderBottom: subTab === 0 ? "3px solid #3b82f6" : "3px solid transparent",
      backgroundColor: subTab === 0 ? "rgba(59,130,246,0.06)" : "transparent",

      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",

      transition: "0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(59,130,246,0.08)",
      },
    }}
  />

  <Tab
    label="One-Time Token Purchase"
    sx={{
      textTransform: "none",
      fontSize: "15px",
      fontWeight: subTab === 1 ? 700 : 500,
      color: subTab === 1 ? "#0f172a" : "#64748b",
      paddingY: 1.5,
      minWidth: 220,

      borderBottom: subTab === 1 ? "3px solid #3b82f6" : "3px solid transparent",
      backgroundColor: subTab === 1 ? "rgba(59,130,246,0.06)" : "transparent",

      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",

      transition: "0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(59,130,246,0.08)",
      },
    }}
  />
</Tabs>


        {/* Regular Subscription */}
        {subTab === 0 && (
          <SubscriptionTypeClean
            numLocations={numLocations}
            selectedPlan={selectedPlan}
            setSelectedPlanExternal={handlePlanSelect}
            plansData={plansData}
            isEdit={true}
            onEditLocations={() => setShowManageLocationsModal(true)}
            currentPlanData={currentPlanData}
            subscriptionStatus={subscriptionStatus}
            nextDueDate={nextDueDate}
          />
        )}

        {/* ONE-TIME TOKEN PURCHASE MODE */}
        {subTab === 1 && (
          <div style={{ display: "flex", gap: "20px" }}>
            {/* LEFT = token packs */}
            <Grid item xs={12} md={6} width={"50%"}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Select Credits Pack
              </Typography>

              {tokenPlans.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Loading available top-up packs...
                </Typography>
              ) : (
                tokenPlans.map((plan) => (
                  <Card
                    key={plan.plan_code}
                    onClick={() => setSelectedTokenPack(plan)}
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: "pointer",
                      border:
                        selectedTokenPack?.plan_code === plan.plan_code
                          ? "2px solid #1976d2"
                          : "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography fontWeight={700}>
                      {plan.plan_name}
                    </Typography>

                    <Typography
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {plan.description}
                    </Typography>

                    <Typography sx={{ mt: 1 }}>
                      <strong>
                        {plan.credits.toLocaleString()}
                      </strong>{" "}
                      credits
                    </Typography>

                    <Typography fontWeight={700} sx={{ mt: 1 }}>
                      ${plan.amount}
                    </Typography>
                  </Card>
                ))
              )}
            </Grid>

            {/* RIGHT = summary box */}
            <Grid item xs={12} md={6} width={"50%"}>
              <SubscriptionTypeClean
                isTokenMode={true}
                selectedTokenPack={
                  selectedTokenPack
                    ? {
                        title: selectedTokenPack.plan_name,
                        tokens: selectedTokenPack.credits,
                        amount: selectedTokenPack.amount,
                      }
                    : null
                }
                plansData={[]}
                numLocations={numLocations}
                isEdit={true}
              />
            </Grid>
          </div>
        )}

        <Stack direction="row" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={() => setActiveTab(0)}>
            Previous
          </Button>

          <Button
            variant="contained"
            onClick={
              subTab === 0 ? () => setActiveTab(1) : handleTokenPurchase
            }
            disabled={subTab === 1 && !selectedTokenPack}
          >
            {subTab === 0 ? "Next" : "Buy Tokens"}
          </Button>
        </Stack>
      </CustomTabPanel>

      {/* -------------------------------------------------- */}
      {/* CHATBOT TAB */}
      {/* -------------------------------------------------- */}
      <CustomTabPanel value={!activeTab} index={2}>
        <ChatbotSettings
          isEdit={true}
          isEditable={isPremiumOrAbove}
          storeId={store_id}
          clientId={CLIENT_ID}
          apiBase={config.API_BASE_URL}
          defaultValues={{
            chatbot_name: chatbotData.chatbot_name,
            welcome_message: chatbotData.chatbot_welcome_message,
            disclaimer_text: chatbotData.chatbot_disclaimer,
          }}
          onSave={(saveFunc) => {
            window.chatbotSaveFunc = saveFunc;
          }}
        />

        <Stack direction="row" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={() => setActiveTab(1)}>
            Previous
          </Button>
          <Button variant="contained" disabled={!isPremiumOrAbove}>
            Save Changes
          </Button>
        </Stack>
      </CustomTabPanel>

      {/* -------------------------------------------------- */}
      {/* BRANDING TAB */}
      {/* -------------------------------------------------- */}
      <CustomTabPanel value={!activeTab} index={3}>
        <BrandingAppearance
          isEdit={true}
          isEditable={isPremiumOrAbove}
          storeId={store_id}
          clientId={CLIENT_ID}
          apiBase={config.API_BASE_URL}
          defaultValues={{
            theme_color_hex: brandingData.chatbot_theme_color,
            font_style: brandingData.chatbot_font_style,
            logo_url: null,
          }}
          onSave={(saveFunc) => {
            window.brandingSaveFunc = saveFunc;
          }}
        />

        <Stack direction="row" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={() => setActiveTab(2)}>
            Previous
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/store-management")}
          >
            Done
          </Button>
        </Stack>
      </CustomTabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((prev) => ({ ...prev, open: false }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modals */}
      <ManageSeatsModal
        open={showManageSeatsModal}
        onClose={() => {
          setShowManageSeatsModal(false);
          setPendingQuantity(null);
        }}
        currentPlan={currentPlanData}
        currentQuantity={numLocations}
        onSave={() => {}}
        subscriptionId={subscriptionId}
      />

      <ManageLocationsModal
        open={showManageLocationsModal}
        onClose={() => setShowManageLocationsModal(false)}
        currentPlan={currentPlanData}
        currentLocations={storeTypes.map((type, index) => ({
          store_type: type,
          zip_code: zipCodes[index] || "",
        }))}
        onSave={() => {}}
        storeId={store_id}
      />

      <PlanChangeModal
        open={showPlanChangeModal}
        onClose={() => {
          setShowPlanChangeModal(false);
          setSelectedPlan(previousPlan); // revert if cancelled/closed
        }}
        currentPlan={currentPlanData}
        newPlan={pendingPlan}
        onConfirm={handleProrationConfirm}
        subscriptionId={subscriptionId}
        storeId={store_id}
        nextDueDate={nextDueDate}
      />

      <AddLocationDetailsModal
        open={showAddLocationModal}
        onClose={() => {
          setShowAddLocationModal(false);
          setPendingQuantity(null);
        }}
        onSave={() => {}}
        numberOfNewLocations={
          pendingQuantity ? pendingQuantity - numLocations : 0
        }
        existingStoreTypes={storeTypes}
        existingZipCodes={zipCodes}
      />
    </Card>
  );
};

export default EditStore;